/**
 * NetworkerResetsPage data module — extracted from the 2100-line parent
 * component so the page file can focus purely on UI + state. Zero logic
 * changes: this file is a direct lift of the constants block from lines
 * 21–598 of the original NetworkerResetsPage.jsx.
 *
 * Exports:
 *   - NATURE_IMAGE_LIBRARY    hero loop imagery by week set
 *   - getTodaysNatureImages() rotates imagery by weekday
 *   - RESET_VOICE_AUDIO       Michael's per-reset voice URLs
 *   - RESETS                  full reset config (timings, lines, icons, etc.)
 *   - SUGGESTIONS             feeling → reset suggestion mapping
 *   - GRID_ORDER              reset grid layout order
 *   - CATEGORY_TABS           filter pills above the reset grid
 */
import {
  Wind, Smile, Eye, Anchor,
  Heart, Brain, Zap, CloudRain, CircleDot, Bolt,
  Leaf, Phone, RotateCcw, Crown
} from 'lucide-react';

// DAILY ROTATING NATURE IMAGE LIBRARY
// Different stunning images each day of the week
// ============================================
export const NATURE_IMAGE_LIBRARY = {
  // Week Set 1 - Mountains & Lakes
  set1: [
    'https://images.unsplash.com/photo-1730216827021-bc1dfa3d9c5b?w=1280&h=720&fit=crop', // Mountain lake sunset
    'https://images.unsplash.com/photo-1644773306512-4136469fc31d?w=1280&h=720&fit=crop', // Mountain lake reflection
    'https://images.unsplash.com/photo-1595259734744-a1d3d69d61f5?w=1280&h=720&fit=crop', // Silhouette trees lake
    'https://images.unsplash.com/photo-1634826080988-9ed0cc121dfe?w=1280&h=720&fit=crop', // Mountains at dusk
    'https://images.pexels.com/photos/1574184/pexels-photo-1574184.jpeg?auto=compress&w=1280', // Wilderness lake mountains
    'https://images.unsplash.com/photo-1715533540804-cd567804e4fe?w=1280&h=720&fit=crop', // Aurora mountains
    'https://images.unsplash.com/photo-1635346245281-14201c7a768a?w=1280&h=720&fit=crop', // Aurora green purple
    'https://images.pexels.com/photos/35919350/pexels-photo-35919350.jpeg?auto=compress&w=1280', // Northern lights
  ],
  // Week Set 2 - Waterfalls & Tropical
  set2: [
    'https://images.unsplash.com/photo-1762279993578-5c214b969360?w=1280&h=720&fit=crop', // Aerial waterfall turquoise
    'https://images.pexels.com/photos/6873552/pexels-photo-6873552.jpeg?auto=compress&w=1280', // Thailand turquoise lagoon
    'https://images.pexels.com/photos/4090139/pexels-photo-4090139.jpeg?auto=compress&w=1280', // Waterfall tropical forest
    'https://images.unsplash.com/photo-1764788799559-824c59d2104a?w=1280&h=720&fit=crop', // Turquoise waterfall pool
    'https://images.unsplash.com/photo-1728932828842-7839cdf57ced?w=1280&h=720&fit=crop', // Large waterfall blue
    'https://images.unsplash.com/photo-1771838556076-ae642abeb584?w=1280&h=720&fit=crop', // Turquoise ocean cloud
    'https://images.unsplash.com/photo-1772797093486-9e170406810e?w=1280&h=720&fit=crop', // Sunlight forest
    'https://images.unsplash.com/photo-1773760008683-a4a2730ba12f?w=1280&h=720&fit=crop', // Milky way
  ],
  // Week Set 3 - Forests & Ethereal
  set3: [
    'https://images.unsplash.com/photo-1767107114836-802e5d2b5e3b?w=1280&h=720&fit=crop', // Sunlight bare trees
    'https://images.unsplash.com/photo-1758554930451-d3bbc1cb4a59?w=1280&h=720&fit=crop', // Misty forest sunbeams
    'https://images.unsplash.com/photo-1768903709732-11743cca896c?w=1280&h=720&fit=crop', // Misty forest canopy
    'https://images.unsplash.com/photo-1764037046783-4ff034b2024b?w=1280&h=720&fit=crop', // Forest sunbeams dense
    'https://images.pexels.com/photos/2191203/pexels-photo-2191203.jpeg?auto=compress&w=1280', // Redwood forest
    'https://images.unsplash.com/photo-1654107296759-46b204d06794?w=1280&h=720&fit=crop', // Milky way stars
    'https://images.unsplash.com/photo-1639849324111-96487086694c?w=1280&h=720&fit=crop', // Starry mountain
    'https://images.unsplash.com/photo-1708476146651-b66d704f643c?w=1280&h=720&fit=crop', // Night sky milky
  ],
  // Week Set 4 - Ocean & Sunset
  set4: [
    'https://images.unsplash.com/photo-1613365890196-76d2e28d77e0?w=1280&h=720&fit=crop', // Rock formation sunset
    'https://images.unsplash.com/photo-1613365891810-b3c06bbcc30c?w=1280&h=720&fit=crop', // Waves crashing sunset
    'https://images.unsplash.com/photo-1613365891889-7f7e3316be61?w=1280&h=720&fit=crop', // Sea waves shore sunset
    'https://images.unsplash.com/photo-1613365892236-0f4523b3503b?w=1280&h=720&fit=crop', // Ocean waves sunset
    'https://images.pexels.com/photos/4603532/pexels-photo-4603532.jpeg?auto=compress&w=1280', // Golden hour beach
    'https://images.pexels.com/photos/6944173/pexels-photo-6944173.jpeg?auto=compress&w=1280', // Sunset ocean waves
    'https://images.unsplash.com/photo-1724700211547-e5d509a9564a?w=1280&h=720&fit=crop', // Aurora lake
    'https://images.pexels.com/photos/6869258/pexels-photo-6869258.jpeg?auto=compress&w=1280', // Norway aurora
  ],
  // Week Set 5 - Spring & Blossom
  set5: [
    'https://images.unsplash.com/photo-1748737350030-bbf7a022c3b5?w=1280&h=720&fit=crop', // Cherry blossom night
    'https://images.unsplash.com/photo-1748737349699-dd87b3d39422?w=1280&h=720&fit=crop', // Cherry blossom lanterns
    'https://images.unsplash.com/photo-1748737349764-45adc860862f?w=1280&h=720&fit=crop', // Cherry blossom sky
    'https://images.pexels.com/photos/2715002/pexels-photo-2715002.jpeg?auto=compress&w=1280', // Mountain lake sunrise
    'https://images.unsplash.com/photo-1716677825305-153fa99b1e39?w=1280&h=720&fit=crop', // Aurora city night
    'https://images.unsplash.com/photo-1715535384818-8e673eb3a620?w=1280&h=720&fit=crop', // Purple aurora
    'https://images.unsplash.com/photo-1654107298576-6e45f7285066?w=1280&h=720&fit=crop', // Tower green field
    'https://images.unsplash.com/photo-1769832880933-eecf2cfe849c?w=1280&h=720&fit=crop', // Mountain lake
  ]
};

// Get today's nature image set (rotates weekly, different each day)
export const getTodaysNatureImages = () => {
  const today = new Date();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  const weekNumber = Math.floor(dayOfYear / 7) % 5; // 5 different sets
  const setKey = `set${weekNumber + 1}`;
  
  // Also shuffle based on day of week for variety within the set
  const dayOfWeek = today.getDay();
  const images = [...NATURE_IMAGE_LIBRARY[setKey]];
  
  // Rotate array by day of week
  const rotated = [...images.slice(dayOfWeek), ...images.slice(0, dayOfWeek)];
  
  return rotated;
};

export const RESET_VOICE_AUDIO = {
  breathe: '/api/audio/voice/reset_breathe.m4a',
  smile: '/api/audio/voice/reset_smile.m4a',
  expand: '/api/audio/voice/reset_expand.m4a',
  drop: '/api/audio/voice/reset_drop.m4a',
  still: '/api/audio/voice/reset_still.m4a',
  activate: '/api/audio/voice/reset_activate.m4a',
  // The Edge — Performance resets
  pre_call: '/api/audio/voice/edge/reset_pre_call.m4a',
  rejection: '/api/audio/voice/edge/reset_rejection.m4a',
  confidence: '/api/audio/voice/edge/reset_confidence.m4a',
};

// Reset configurations - phases timed to match Michael's NEW recordings exactly
export const RESETS = {
  breathe: {
    id: 'breathe',
    category: 'calm',
    name: 'Breathe',
    tagline: 'Return to centre',
    icon: Wind,
    color: 'from-sky-400 to-blue-500',
    bgGradient: 'from-[#1A2D4A] to-[#0F1A2D]',
    accentColor: 'rgba(100, 160, 220, 0.35)',
    glowColor: 'rgba(100, 160, 220, 0.5)',
    motionType: 'simple-breathe',
    audioDuration: 94,
    suggestion: 'Stressed',
    closingMessage: 'Carry this centredness with you into your day',
    voiceAudio: RESET_VOICE_AUDIO.breathe,
    phases: [
      { start: 0, text: '', instruction: '', animation: 'neutral' },
      { start: 2.8, text: 'Find a comfortable position', instruction: '', animation: 'neutral' },
      { start: 5.6, text: 'Let your body settle', instruction: '', animation: 'neutral' },
      // Breath 1
      { start: 9.5, text: 'Breathe in...', instruction: '', animation: 'expand' },
      { start: 14.7, text: 'Hold...', instruction: '', animation: 'hold' },
      { start: 20.4, text: 'And release...', instruction: '', animation: 'contract' },
      { start: 25.1, text: 'And breathe normally...', instruction: '', animation: 'neutral' },
      // Breath 2
      { start: 32.1, text: 'Breathe in...', instruction: '', animation: 'expand' },
      { start: 38.1, text: 'Hold...', instruction: '', animation: 'hold' },
      { start: 43.9, text: 'And release...', instruction: '', animation: 'contract' },
      { start: 48.9, text: 'And breathe normally...', instruction: '', animation: 'neutral' },
      // Breath 3
      { start: 55.5, text: 'Breathe in...', instruction: '', animation: 'expand' },
      { start: 61.0, text: 'Hold...', instruction: '', animation: 'hold' },
      { start: 66.5, text: 'And release...', instruction: '', animation: 'contract' },
      { start: 71.5, text: 'And breathe normally...', instruction: '', animation: 'neutral' },
      // Closing
      { start: 75.4, text: 'Feel the calm...', instruction: '', animation: 'neutral' },
      { start: 78.9, text: 'Notice the shift...', instruction: '', animation: 'neutral' },
      { start: 86.5, text: 'Carry this centredness with you into your day', instruction: '', animation: 'neutral' }
    ]
  },
  smile: {
    id: 'smile',
    category: 'calm',
    name: 'Smile',
    tagline: 'Soften and lift',
    icon: Smile,
    color: 'from-amber-400 to-orange-500',
    bgGradient: 'from-amber-900/95 via-orange-900/90 to-slate-900',
    accentColor: 'rgba(251, 191, 36, 0.35)',
    glowColor: 'rgba(251, 191, 36, 0.5)',
    motionType: 'radiate',
    audioDuration: 78,
    suggestion: 'Low',
    closingMessage: 'Carry this lightness with you into your day',
    voiceAudio: RESET_VOICE_AUDIO.smile,
    // SYNCED to Michael's voice (analyzed with pydub)
    phases: [
      { start: 0, text: '', instruction: '', animation: 'neutral' },
      { start: 5.0, text: 'Pause for a moment...', instruction: '', animation: 'neutral' },
      { start: 7.1, text: 'Be still...', instruction: '', animation: 'pulse' },
      { start: 10.5, text: 'Gently bring a soft smile to your face...', instruction: '', animation: 'glow' },
      { start: 18.7, text: 'Let it reach your eyes...', instruction: '', animation: 'glow' },
      { start: 27.6, text: 'Think of one thing you\'re grateful for...', instruction: '', animation: 'expand' },
      { start: 30.8, text: 'Feel the gratitude...', instruction: '', animation: 'radiate' },
      { start: 41.6, text: 'Feel the warmth spreading through your chest...', instruction: '', animation: 'radiate' },
      { start: 51.9, text: 'Let it flow through your whole body...', instruction: '', animation: 'radiate' },
      { start: 61.5, text: 'Hold this feeling...', instruction: '', animation: 'glow' },
      { start: 72.4, text: '', instruction: '', animation: 'neutral' }
    ]
  },
  expand: {
    id: 'expand',
    category: 'calm',
    name: 'Expand',
    tagline: 'Widen your view',
    icon: Eye,
    color: 'from-violet-500 to-purple-600',
    bgGradient: 'from-violet-900/95 via-purple-900/90 to-slate-900',
    accentColor: 'rgba(139, 92, 246, 0.3)',
    glowColor: 'rgba(139, 92, 246, 0.5)',
    motionType: 'ripple-expand',
    audioDuration: 99,
    suggestion: 'Tense',
    closingMessage: 'Take this into your day',
    voiceAudio: RESET_VOICE_AUDIO.expand,
    phases: [
      { start: 0, text: '', instruction: '', animation: 'neutral' },
      { start: 2.5, text: 'Pause and be present...', instruction: '', animation: 'neutral' },
      { start: 5.0, text: 'Arrive here...', instruction: '', animation: 'pulse' },
      { start: 10.1, text: 'Take a slow breath into your belly...', instruction: '', animation: 'expand' },
      { start: 19.0, text: 'Soften your gaze...', instruction: '', animation: 'soften' },
      { start: 23.2, text: 'Relax your eyes...', instruction: '', animation: 'soften' },
      { start: 32.3, text: 'Keep looking forward but relax your focus...', instruction: '', animation: 'soften' },
      { start: 44.7, text: 'Notice what\'s in your peripheral vision...', instruction: '', animation: 'expand' },
      { start: 47.6, text: 'Expand your awareness...', instruction: '', animation: 'expand' },
      { start: 62.0, text: 'Expand your awareness wider...', instruction: '', animation: 'expand-wide' },
      { start: 74.6, text: 'See everything at once...', instruction: '', animation: 'expand-wide' },
      { start: 78.6, text: 'Hold this open, relaxed state...', instruction: '', animation: 'hold' },
      { start: 88.0, text: 'You are calm, aware, and present...', instruction: '', animation: 'neutral' },
      { start: 93, text: '', instruction: '', animation: 'neutral' }
    ]
  },
  drop: {
    id: 'drop',
    category: 'calm',
    name: 'Drop',
    tagline: 'Come back to the body',
    icon: Anchor,
    color: 'from-emerald-500 to-teal-600',
    bgGradient: 'from-emerald-950/95 via-teal-900/95 to-slate-950',
    accentColor: 'rgba(16, 185, 129, 0.3)',
    glowColor: 'rgba(16, 185, 129, 0.5)',
    motionType: 'settle-particles',
    audioDuration: 84,
    suggestion: 'Overthinking',
    closingMessage: 'Take this presence into your day',
    voiceAudio: RESET_VOICE_AUDIO.drop,
    phases: [
      { start: 0, text: '', instruction: '', animation: 'neutral' },
      { start: 1.8, text: 'Stop...', instruction: '', animation: 'stop' },
      { start: 3.4, text: 'Pause completely...', instruction: '', animation: 'stop' },
      { start: 9.1, text: 'Feel your feet on the ground...', instruction: '', animation: 'ground' },
      { start: 16.2, text: 'Notice your hands...', instruction: '', animation: 'settle' },
      { start: 18.8, text: 'Notice their weight...', instruction: '', animation: 'settle' },
      { start: 26.1, text: 'Drop your shoulders...', instruction: '', animation: 'drop' },
      { start: 36.3, text: 'Unclench your jaw...', instruction: '', animation: 'soften' },
      { start: 38.9, text: 'Soften your face...', instruction: '', animation: 'soften' },
      { start: 47.0, text: 'Breathe low into your belly...', instruction: '', animation: 'settle' },
      { start: 56.5, text: 'Feel your weight...', instruction: '', animation: 'gravity' },
      { start: 59.1, text: 'Let gravity hold you...', instruction: '', animation: 'gravity' },
      { start: 62.4, text: 'Drop deep into your body...', instruction: '', animation: 'deep-drop' },
      { start: 71.0, text: 'You are here...', instruction: '', animation: 'neutral' },
      { start: 73.0, text: 'You are grounded...', instruction: '', animation: 'neutral' },
      { start: 76.0, text: 'You are present...', instruction: '', animation: 'neutral' },
      { start: 79, text: '', instruction: '', animation: 'neutral' }
    ]
  },
  still: {
    id: 'still',
    category: 'sleep',
    name: 'Still',
    tagline: 'Find the space between thoughts',
    icon: CircleDot,
    color: 'from-indigo-400 to-violet-500',
    bgGradient: 'from-[#0a0a1a] via-indigo-950 to-[#0d0d1f]',
    accentColor: 'rgba(99, 102, 241, 0.3)',
    glowColor: 'rgba(139, 92, 246, 0.5)',
    motionType: 'shimmer-still',
    audioDuration: 108,
    suggestion: 'Racing thoughts',
    closingMessage: 'Carry this stillness with you into your day',
    voiceAudio: RESET_VOICE_AUDIO.still,
    // SYNCED to Michael's voice
    phases: [
      { start: 0, text: '', instruction: '', animation: 'neutral' },
      { start: 1.6, text: 'Pause...', instruction: '', animation: 'neutral' },
      { start: 3.7, text: 'Stop...', instruction: '', animation: 'stop' },
      { start: 8.2, text: 'Take a slow breath into your belly...', instruction: '', animation: 'expand' },
      { start: 16.4, text: 'And gently release...', instruction: '', animation: 'contract' },
      { start: 26.3, text: 'Again...', instruction: '', animation: 'neutral' },
      { start: 29.0, text: 'Slow breath...', instruction: '', animation: 'expand' },
      { start: 36.5, text: 'And release...', instruction: '', animation: 'contract' },
      { start: 38.5, text: 'Let go...', instruction: '', animation: 'contract' },
      { start: 44.7, text: 'Now, silently ask yourself...', instruction: '', animation: 'neutral' },
      { start: 48.6, text: 'I wonder what my next thought will be...', instruction: '', animation: 'observe' },
      { start: 59.5, text: 'Just wait...', instruction: '', animation: 'still' },
      { start: 69.4, text: 'Notice the space...', instruction: '', animation: 'still' },
      { start: 71.9, text: 'The stillness between thoughts...', instruction: '', animation: 'still' },
      { start: 82.2, text: 'Rest here in that...', instruction: '', animation: 'rest' },
      { start: 92.5, text: 'Take one more slow breath...', instruction: '', animation: 'expand' },
      { start: 102.7, text: '', instruction: '', animation: 'neutral' }
    ]
  },
  activate: {
    id: 'activate',
    category: 'calm',
    name: 'Activate',
    tagline: 'Rise and ignite',
    icon: Bolt,
    color: 'from-orange-500 to-red-500',
    bgGradient: 'from-orange-900/95 via-red-900/90 to-slate-900',
    accentColor: 'rgba(249, 115, 22, 0.35)',
    glowColor: 'rgba(249, 115, 22, 0.6)',
    motionType: 'energize-rise',
    audioDuration: 97,
    suggestion: 'Flat',
    closingMessage: 'Step forward with energy, fully activated',
    voiceAudio: RESET_VOICE_AUDIO.activate,
    // SYNCED to Michael's voice
    phases: [
      { start: 0, text: '', instruction: '', animation: 'ready' },
      { start: 1.6, text: 'Pause...', instruction: '', animation: 'ready' },
      { start: 3.0, text: 'Get ready...', instruction: '', animation: 'ready' },
      { start: 7.1, text: 'Take a deep breath in through your nose...', instruction: '', animation: 'power-in' },
      { start: 13.9, text: 'Exhale strongly through your mouth...', instruction: '', animation: 'power-out' },
      { start: 21.1, text: 'Again, deeper...', instruction: '', animation: 'neutral' },
      { start: 24.2, text: 'Inhale...', instruction: '', animation: 'power-in' },
      { start: 30.6, text: 'And release strongly...', instruction: '', animation: 'power-out' },
      { start: 36.0, text: 'Straighten your posture...', instruction: '', animation: 'rise' },
      { start: 38.0, text: 'Stand tall...', instruction: '', animation: 'rise' },
      { start: 43.6, text: 'Lift your chest slightly...', instruction: '', animation: 'expand' },
      { start: 45.3, text: 'Open it up...', instruction: '', animation: 'expand' },
      { start: 51.1, text: 'Feel your whole body becoming more alert...', instruction: '', animation: 'energize' },
      { start: 60.6, text: 'Now take another strong breath in...', instruction: '', animation: 'power-in' },
      { start: 68.6, text: 'Bring your awareness to this moment...', instruction: '', animation: 'ready' },
      { start: 70.5, text: 'Clear. Focused. Ready.', instruction: '', animation: 'ready' },
      { start: 77.4, text: 'Feel the energy building...', instruction: '', animation: 'surge' },
      { start: 88, text: '', instruction: '', animation: 'activated' },
      { start: 90, text: 'Fully activated.', instruction: '', animation: 'activated' }
    ]
  },
  letgo: {
    id: 'letgo',
    category: 'calm',
    name: 'Let Go',
    tagline: 'Release and soften',
    icon: Wind,
    color: 'from-sky-400 to-cyan-500',
    bgGradient: 'from-sky-950/95 via-cyan-900/90 to-slate-950',
    accentColor: 'rgba(56, 189, 248, 0.35)',
    glowColor: 'rgba(103, 232, 249, 0.5)',
    motionType: 'shoulder-release',
    audioDuration: 92,
    suggestion: 'Holding tension',
    closingMessage: 'Nothing to hold. Just soften and be.',
    voiceAudio: '/api/audio/voice/reset_letgo.m4a',
    // PERFECTLY SYNCED to Michael's voice recording (analyzed with pydub)
    phases: [
      { start: 0, text: '', instruction: '', animation: 'neutral' },
      { start: 0.5, text: 'Pause...', instruction: '', animation: 'neutral' },
      { start: 3.4, text: 'Take a deep breath in...', instruction: '', animation: 'neutral' },
      { start: 8.4, text: 'Gently lift up your shoulders...', instruction: '', animation: 'lift' },
      { start: 14.4, text: 'Lift them up even more...', instruction: '', animation: 'lift-higher' },
      { start: 20.2, text: 'Hold...', instruction: '', animation: 'hold-high' },
      { start: 24.4, text: 'And let out a sigh as you feel them drop...', instruction: '', animation: 'release-sigh' },
      { start: 30.5, text: 'Completely let go...', instruction: '', animation: 'soften' },
      { start: 36.8, text: 'Feel all the tension drain away...', instruction: '', animation: 'soften' },
      { start: 42.6, text: 'Again...', instruction: '', animation: 'neutral' },
      { start: 44.6, text: 'Gently lift up your shoulders...', instruction: '', animation: 'lift' },
      { start: 48.7, text: 'Lift them up even more...', instruction: '', animation: 'lift-higher' },
      { start: 52.5, text: 'Hold...', instruction: '', animation: 'hold-high' },
      { start: 55.7, text: 'And let out a sigh as you feel them drop...', instruction: '', animation: 'release-sigh' },
      { start: 60.9, text: 'Softening...', instruction: '', animation: 'soften' },
      { start: 62.2, text: 'releasing...', instruction: '', animation: 'soften' },
      { start: 63.3, text: 'letting go...', instruction: '', animation: 'soften' },
      { start: 66.9, text: 'Feel this relaxation move through your body...', instruction: '', animation: 'settle' },
      { start: 76.0, text: 'Nothing to hold...', instruction: '', animation: 'neutral' },
      { start: 78.5, text: 'Just soften... and be...', instruction: '', animation: 'neutral' },
      { start: 85, text: '', instruction: '', animation: 'closing' }
    ]
  },
  heart: {
    id: 'heart',
    category: 'calm',
    name: 'Heart',
    tagline: 'Return to love',
    icon: Heart,
    color: 'from-rose-400 to-pink-500',
    bgGradient: 'from-rose-950/95 via-pink-900/90 to-slate-950',
    accentColor: 'rgba(251, 113, 133, 0.35)',
    glowColor: 'rgba(253, 164, 175, 0.5)',
    motionType: 'heart-expand',
    audioDuration: 102,
    suggestion: 'Disconnected',
    closingMessage: 'Return to your heart... and carry this love with you into your day.',
    voiceAudio: '/api/audio/voice/reset_heart.m4a',
    // PERFECTLY SYNCED to Michael's voice recording (analyzed with pydub)
    phases: [
      { start: 0, text: '', instruction: '', animation: 'neutral' },
      { start: 0.7, text: 'Pause...', instruction: '', animation: 'neutral' },
      { start: 4.4, text: 'Gently close your eyes...', instruction: '', animation: 'soften' },
      { start: 9.5, text: 'Slowly breathe...', instruction: '', animation: 'belly-breathe' },
      { start: 14.6, text: 'Deep...', instruction: '', animation: 'belly-breathe' },
      { start: 15.3, text: 'soft...', instruction: '', animation: 'belly-breathe' },
      { start: 16.5, text: 'natural...', instruction: '', animation: 'belly-breathe' },
      { start: 20.5, text: 'Allow that breath... that energy...', instruction: '', animation: 'rise-to-heart' },
      { start: 25.6, text: 'To rise up into your heart space...', instruction: '', animation: 'rise-to-heart' },
      { start: 31.7, text: 'Feel your chest gently expand...', instruction: '', animation: 'heart-expand' },
      { start: 36.6, text: 'Feel the warmth...', instruction: '', animation: 'heart-glow' },
      { start: 40.7, text: 'The openness...', instruction: '', animation: 'heart-glow' },
      { start: 45.7, text: 'Now tune into something that makes you truly grateful...', instruction: '', animation: 'heart-glow' },
      { start: 52.6, text: 'Someone you love...', instruction: '', animation: 'heart-radiate' },
      { start: 56.6, text: 'A moment...', instruction: '', animation: 'heart-radiate' },
      { start: 59.9, text: 'A simple blessing in your life...', instruction: '', animation: 'heart-radiate' },
      { start: 65.9, text: 'Breathe that feeling into your heart...', instruction: '', animation: 'heart-fill' },
      { start: 72.8, text: 'And let it gently spread through your body...', instruction: '', animation: 'heart-spread' },
      { start: 81.3, text: 'Rest here...', instruction: '', animation: 'heart-rest' },
      { start: 91.4, text: 'Return to your heart... and carry this love with you...', instruction: '', animation: 'neutral' },
      { start: 98, text: '', instruction: '', animation: 'closing' }
    ]
  },
  bathe: {
    id: 'bathe',
    category: 'sleep',
    name: 'Bathe',
    tagline: 'Let beauty in',
    icon: Leaf,
    color: 'from-emerald-400 to-teal-500',
    bgGradient: 'from-emerald-950/95 via-teal-900/90 to-slate-950',
    accentColor: 'rgba(52, 211, 153, 0.35)',
    glowColor: 'rgba(110, 231, 183, 0.5)',
    motionType: 'nature-bathe',
    audioDuration: 75,
    suggestion: 'Need beauty',
    closingMessage: 'Carry this beauty with you into your day.',
    voiceAudio: '/api/audio/ambient/safe_linen_air.mp3', // Suno calm-presence pad
    // Nature slideshow - uses daily rotating images from library
    get slides() {
      return getTodaysNatureImages();
    },
    phases: [
      { start: 0, text: '', instruction: '', animation: 'arrive', slideIndex: 0 },
      { start: 2, text: 'Take a deep breath in...', instruction: '', animation: 'breathe', slideIndex: 0 },
      { start: 6, text: 'And release...', instruction: '', animation: 'release', slideIndex: 0 },
      { start: 10, text: '', instruction: '', animation: 'slide', slideIndex: 1 },
      { start: 14, text: 'Nothing to do...', instruction: '', animation: 'slide', slideIndex: 1 },
      { start: 18, text: 'Except bathe in the majesty of nature...', instruction: '', animation: 'slide', slideIndex: 1 },
      { start: 24, text: '', instruction: '', animation: 'slide', slideIndex: 2 },
      { start: 32, text: 'Let the beauty wash over you...', instruction: '', animation: 'slide', slideIndex: 3 },
      { start: 40, text: '', instruction: '', animation: 'slide', slideIndex: 4 },
      { start: 48, text: 'Feel the love... the wonder... the peace...', instruction: '', animation: 'slide', slideIndex: 5 },
      { start: 56, text: '', instruction: '', animation: 'slide', slideIndex: 6 },
      { start: 62, text: 'This beauty is always here... waiting for you...', instruction: '', animation: 'slide', slideIndex: 7 },
      { start: 68, text: '', instruction: '', animation: 'closing', slideIndex: 7 }
    ]
  },

  // ─── THE EDGE — Performance Resets ──────────────────────────────
  // Spec: short (1–3 mins), tool-like, immediate state shift. Tagline
  // and microcopy framed as "before / after a moment that matters".

  pre_call: {
    id: 'pre_call',
    category: 'performance',
    name: 'Pre-Call Reset',
    tagline: 'Before your next call',
    icon: Phone,
    color: 'from-amber-500 to-orange-600',
    bgGradient: 'from-[#1A1410] to-[#0E0B08]',
    accentColor: 'rgba(217, 165, 90, 0.32)',
    glowColor: 'rgba(217, 165, 90, 0.45)',
    motionType: 'simple-breathe',
    audioDuration: 113,
    suggestion: 'Performance',
    closingMessage: 'Carry this presence into the call',
    voiceAudio: RESET_VOICE_AUDIO.pre_call,
    // Phase timings auto-synced via scripts/sync_state_tuning.py — Whisper
    // segment alignment to the actual 112.5s recording.
    phases: [
      { start: 0,     text: '',                                                              instruction: '',         animation: 'neutral'  },
      { start: 0.0,   text: 'Pause. Breathe in. And release.',                               instruction: 'Breathe',  animation: 'inhale'   },
      { start: 12.0,  text: 'Roll your shoulders back slightly. Lift your chest.',           instruction: '',         animation: 'neutral'  },
      { start: 22.8,  text: 'Relax your jaw. You don\u2019t need to impress.',               instruction: '',         animation: 'neutral'  },
      { start: 30.0,  text: 'You don\u2019t need to prove. You are already enough.',         instruction: '',         animation: 'identity' },
      { start: 37.88, text: 'Now feel this. I am calm. I am clear. I am grounded.',          instruction: '',         animation: 'identity' },
      { start: 47.0,  text: 'People feel my presence. People trust my energy.',              instruction: '',         animation: 'identity' },
      { start: 54.96, text: 'I lead this moment. Everything slows down in the best way.',    instruction: '',         animation: 'identity' },
      { start: 65.0,  text: 'Now just imagine the call.',                                    instruction: '',         animation: 'neutral'  },
      { start: 74.0,  text: 'You speak in clearly. Naturally. Confidently.',                 instruction: '',         animation: 'identity' },
      { start: 88.0,  text: 'No force. No tension. Just flow.',                              instruction: '',         animation: 'identity' },
      { start: 101.44,text: 'Lock it in. I am ready. Go.',                                   instruction: '',         animation: 'closing'  },
    ]
  },

  rejection: {
    id: 'rejection',
    category: 'performance',
    name: 'Rejection Reset',
    tagline: 'Return to forward motion',
    icon: RotateCcw,
    color: 'from-amber-500 to-rose-500',
    bgGradient: 'from-[#1A1014] to-[#0D080B]',
    accentColor: 'rgba(220, 130, 130, 0.30)',
    glowColor: 'rgba(220, 130, 130, 0.45)',
    motionType: 'simple-breathe',
    audioDuration: 89,
    suggestion: 'Performance',
    closingMessage: 'You\u2019re back. Keep moving.',
    voiceAudio: RESET_VOICE_AUDIO.rejection,
    // Phase timings auto-synced via scripts/sync_state_tuning.py — Whisper
    // segment alignment to the actual recording, not hand-tuned drift.
    phases: [
      { start: 0,    text: '',                                                            instruction: '',         animation: 'neutral'  },
      { start: 0.0,  text: 'Take a breath. And release.',                                 instruction: 'Breathe',  animation: 'inhale'   },
      { start: 8.0,  text: 'That moment has passed. It does not define you.',             instruction: '',         animation: 'neutral'  },
      { start: 20.2, text: 'Feel your body again. Relax your shoulders.',                 instruction: '',         animation: 'neutral'  },
      { start: 33.8, text: 'This is part of the process. This is movement.',              instruction: '',         animation: 'identity' },
      { start: 49.36,text: 'Every no is creating space for a yes.',                       instruction: '',         animation: 'identity' },
      { start: 60.0, text: 'You are still moving forward. You are still building.',       instruction: '',         animation: 'identity' },
      { start: 72.36,text: 'I stay steady. I stay open. I keep moving.',                  instruction: '',         animation: 'identity' },
      { start: 82.0, text: 'Take one more breath in… and reset. You\u2019re back.',      instruction: 'Breathe',  animation: 'closing'  },
    ]
  },

  confidence: {
    id: 'confidence',
    category: 'performance',
    name: 'Confidence Reset',
    tagline: 'Lead from your edge',
    icon: Crown,
    color: 'from-amber-400 to-yellow-600',
    bgGradient: 'from-[#181410] to-[#0C0A07]',
    accentColor: 'rgba(220, 180, 110, 0.32)',
    glowColor: 'rgba(220, 180, 110, 0.50)',
    motionType: 'simple-breathe',
    audioDuration: 99,
    suggestion: 'Performance',
    closingMessage: 'Step forward. This comes with you.',
    voiceAudio: RESET_VOICE_AUDIO.confidence,
    // Phase timings synchronised to the actual recording (Whisper).
    phases: [
      { start: 0,   text: '',                                            instruction: '',         animation: 'neutral'  },
      { start: 1,   text: 'Breathe in',                                  instruction: 'Breathe',  animation: 'inhale'   },
      { start: 7,   text: '\u2026and release',                           instruction: 'Release',  animation: 'exhale'   },
      { start: 14,  text: 'Let everything slow down',                    instruction: '',         animation: 'neutral'  },
      { start: 19,  text: 'Nothing to chase, nothing to prove',          instruction: '',         animation: 'neutral'  },
      { start: 27,  text: 'Just arrive fully in yourself',               instruction: '',         animation: 'neutral'  },
      { start: 32,  text: 'Feel your body. Feel your breath.',           instruction: '',         animation: 'neutral'  },
      { start: 41,  text: 'When you are calm, you are powerful',         instruction: '',         animation: 'identity' },
      { start: 49,  text: 'When you are grounded, people feel it',      instruction: '',         animation: 'identity' },
      { start: 55,  text: 'You don\u2019t need to speak louder',         instruction: '',         animation: 'neutral'  },
      { start: 58,  text: 'You don\u2019t need to do more',              instruction: '',         animation: 'neutral'  },
      { start: 61,  text: 'The presence does the work',                  instruction: '',         animation: 'identity' },
      { start: 65,  text: 'Step into that identity',                     instruction: '',         animation: 'identity' },
      { start: 70,  text: 'I am steady',                                 instruction: '',         animation: 'identity' },
      { start: 74,  text: 'I am certain',                                instruction: '',         animation: 'identity' },
      { start: 78,  text: 'I am trusted',                                instruction: '',         animation: 'identity' },
      { start: 82,  text: 'Feel that land',                              instruction: '',         animation: 'neutral'  },
      { start: 86,  text: 'Wherever you go, this comes with you',        instruction: '',         animation: 'identity' },
      { start: 92,  text: 'Take a breath',                               instruction: 'Breathe',  animation: 'inhale'   },
      { start: 95,  text: 'Step forward',                                instruction: '',         animation: 'closing'  },
    ]
  }
};

// Suggestion mapping
export const SUGGESTIONS = [
  { feeling: 'Stressed', reset: 'breathe', icon: CloudRain },
  { feeling: 'Low', reset: 'smile', icon: Heart },
  { feeling: 'Overthinking', reset: 'drop', icon: Brain },
  { feeling: 'Tense', reset: 'expand', icon: Zap },
  { feeling: 'Racing thoughts', reset: 'still', icon: CircleDot },
  { feeling: 'Flat', reset: 'activate', icon: Bolt },
  { feeling: 'Holding tension', reset: 'letgo', icon: Wind },
  { feeling: 'Disconnected', reset: 'heart', icon: Heart },
  { feeling: 'Need beauty', reset: 'bathe', icon: Leaf }
];

// Grid order for layout - Performance row first (Pre-Call / Rejection /
// Confidence) per the EDGE behavioural spec, then Calm/Sleep below.
export const GRID_ORDER = ['pre_call', 'rejection', 'confidence', 'breathe', 'smile', 'activate', 'expand', 'drop', 'still', 'letgo', 'heart', 'bathe'];

// Filter categories shown above the grid (Edge spec).
export const CATEGORY_TABS = [
  { id: 'all', label: 'All' },
  { id: 'calm', label: 'Calm' },
  { id: 'sleep', label: 'Sleep' },
  { id: 'performance', label: 'Performance' },
];
